import React, { useEffect, useState } from "react";
import { api, ApiError, type MaintenanceRequest, type RequestCategory, type VisitRequest, type VacateRequest, type Tenancy } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { fileToResizedDataUrl } from "../../photoUtils";
import { RequestDetail } from "../shared/RequestDetail";

const VACATE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const VISIT_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const MAINTENANCE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const STATUS_KEY: Record<string, string> = {
  open: "common.statusOpen",
  in_progress: "common.statusInProgress",
  resolved: "common.statusResolved",
};

const CATEGORIES: RequestCategory[] = ["plumbing", "electrical", "appliance", "furniture", "cleaning", "pest_control", "other"];
const CATEGORY_KEY: Record<RequestCategory, string> = {
  plumbing: "requests.categoryPlumbing",
  electrical: "requests.categoryElectrical",
  appliance: "requests.categoryAppliance",
  furniture: "requests.categoryFurniture",
  cleaning: "requests.categoryCleaning",
  pest_control: "requests.categoryPestControl",
  other: "requests.categoryOther",
};

const VISIT_STATUS_KEY: Record<string, string> = {
  pending: "tenantRequests.visitStatusPending",
  confirmed: "tenantRequests.visitStatusConfirmed",
  declined: "tenantRequests.visitStatusDeclined",
  cancelled: "tenantRequests.visitStatusCancelled",
};

const VACATE_STATUS_KEY: Record<string, string> = {
  pending: "tenantRequests.vacateStatusPending",
  acknowledged: "tenantRequests.vacateStatusAcknowledged",
  cancelled: "tenantRequests.vacateStatusCancelled",
};

function fmtDate(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const VALID_TABS = new Set(["maintenance", "visits", "vacate"]);

export function TenantRequests({ focusTab }: { focusTab?: string | null } = {}) {
  const { t } = useLanguage();
  const [requests, setRequests] = useState<MaintenanceRequest[] | null>(null);
  const [visits, setVisits] = useState<VisitRequest[] | null>(null);
  const [tenancies, setTenancies] = useState<Tenancy[] | null>(null);
  const [vacateRequests, setVacateRequests] = useState<VacateRequest[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<RequestCategory | null>(null);
  const [urgent, setUrgent] = useState(false);
  const [photo, setPhoto] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [openRequestId, setOpenRequestId] = useState<string | null>(null);
  // A notification deep link (e.g. a visit confirmation) opens straight to
  // the tab it's about instead of always landing on Maintenance.
  const [tab, setTab] = useState<"maintenance" | "visits" | "vacate">(
    () => (focusTab && VALID_TABS.has(focusTab) ? (focusTab as any) : "maintenance")
  );
  const toast = useToast();

  async function load() {
    const [{ requests }, { visits }, { tenancies }, { vacateRequests }] = await Promise.all([
      api.get("/api/requests/mine"),
      api.get("/api/visits/mine"),
      api.get("/api/tenancies/mine"),
      api.get("/api/vacate-requests/mine"),
    ]);
    setRequests(requests);
    setVisits(visits);
    setTenancies(tenancies);
    setVacateRequests(vacateRequests);
  }

  useEffect(() => {
    load();
  }, []);

  async function cancelVisit(id: string) {
    await api.patch(`/api/visits/${id}`, { status: "cancelled" });
    toast(t("tenantRequests.visitCancelledToast"));
    load();
  }

  async function handlePhotoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) return;
    setPhotoBusy(true);
    try {
      setPhoto(await fileToResizedDataUrl(file));
    } finally {
      setPhotoBusy(false);
    }
  }

  async function submit() {
    if (!title.trim()) return setError(t("tenantRequests.giveTitleError"));
    setBusy(true);
    setError("");
    try {
      await api.post("/api/requests", { title, description, category, urgency: urgent ? "urgent" : "normal", photo: photo || null });
      setAdding(false);
      setTitle("");
      setDescription("");
      setCategory(null);
      setUrgent(false);
      setPhoto("");
      toast(t("tenantRequests.requestRaisedToast"));
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  if (!requests || !visits || !tenancies || !vacateRequests) return <div className="spinner" />;

  if (openRequestId) {
    return <RequestDetail requestId={openRequestId} onBack={() => setOpenRequestId(null)} onChanged={load} />;
  }

  const activeTenancy = tenancies[0] ?? null;
  const pendingVisits = visits.filter((v) => v.status === "pending");
  const openRequestCount = requests.filter((r) => r.status !== "resolved").length;
  const pendingVacate = vacateRequests.filter((v) => v.status === "pending").length;

  return (
    <>
      <div className="seg-control">
        <button className={`seg-btn${tab === "maintenance" ? " active" : ""}`} onClick={() => setTab("maintenance")}>
          {t("tenantRequests.repairsTab")}
          {openRequestCount > 0 && <span className="seg-count">{openRequestCount}</span>}
        </button>
        <button className={`seg-btn${tab === "visits" ? " active" : ""}`} onClick={() => setTab("visits")}>
          {t("tenantRequests.visitsTab")}
          {pendingVisits.length > 0 && <span className="seg-count">{pendingVisits.length}</span>}
        </button>
        {(activeTenancy || vacateRequests.length > 0) && (
          <button className={`seg-btn${tab === "vacate" ? " active" : ""}`} onClick={() => setTab("vacate")}>
            {t("tenantRequests.vacateTab")}
            {pendingVacate > 0 && <span className="seg-count">{pendingVacate}</span>}
          </button>
        )}
      </div>

      {tab === "maintenance" && (
        <>
          <div className="section-title" style={{ marginTop: 0 }}>
            {t("tenantRequests.title")}
          </div>

          {!adding && (
            <div className="empty-add-cta" style={{ padding: "16px 12px 24px" }}>
              <button onClick={() => setAdding(true)} aria-label={t("tenantRequests.raiseRequestCta")}>+</button>
              <span>{t("tenantRequests.raiseRequestCta")}</span>
            </div>
          )}

          {adding && (
            <div className="card">
              <div className="card-row" style={{ marginBottom: 4 }}>
                <b style={{ fontSize: 13.5 }}>{t("tenantRequests.raiseRequestCta")}</b>
                <button type="button" className="link-btn" onClick={() => setAdding(false)}>
                  {t("tenantRequests.cancelLink")}
                </button>
              </div>
              {error && <div className="error-banner">{error}</div>}
              <div className="field">
                <label>{t("tenantRequests.whatsWrong")}</label>
                <div className="input-wrap"><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("tenantRequests.issuePlaceholder")} /></div>
              </div>
              <div className="field">
                <label>{t("tenantRequests.detailsOptional")}</label>
                <div className="input-wrap"><textarea value={description} onChange={(e) => setDescription(e.target.value)} /></div>
              </div>
              <div className="field">
                <label>{t("requests.categoryLabel")}</label>
                <div className="chip-row-scroll">
                  {CATEGORIES.map((c) => (
                    <div key={c} className={`chip${category === c ? " selected" : ""}`} onClick={() => setCategory(category === c ? null : c)}>
                      {t(CATEGORY_KEY[c])}
                    </div>
                  ))}
                </div>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 16px" }}>
                <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
                <span className="sub">{t("requests.urgentCheckboxLabel")}</span>
              </label>
              <div className="field">
                <label>{t("requests.photoLabel")}</label>
                <input type="file" accept="image/*" id="request-photo-input" style={{ display: "none" }} onChange={handlePhotoFile} />
                <div className="aadhaar-slots" style={{ maxWidth: 140 }}>
                  {photo ? (
                    <div className="aadhaar-slot">
                      <div className="aadhaar-slot-filled">
                        <img src={photo} alt="" onClick={() => document.getElementById("request-photo-input")?.click()} />
                        <button type="button" className="aadhaar-slot-remove" onClick={() => setPhoto("")} aria-label={t("requests.removePhotoAria")}>
                          &times;
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="aadhaar-slot-add" disabled={photoBusy} onClick={() => document.getElementById("request-photo-input")?.click()}>
                      <span>{photoBusy ? "…" : "+"}</span>
                      <small>{t("requests.addPhotoBtn")}</small>
                    </button>
                  )}
                </div>
              </div>
              <button className="btn-solid" disabled={busy} onClick={submit}>
                {busy ? t("tenantRequests.submittingBtn") : t("tenantRequests.submitRequestBtn")}
              </button>
            </div>
          )}

          {requests.length === 0 && !adding && <div className="empty-state">{t("tenantRequests.noRequestsYet")}</div>}
          {requests.map((r) => (
            <div className="req-card maintenance" key={r.id} onClick={() => setOpenRequestId(r.id)} style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div className="req-icon maintenance">{MAINTENANCE_ICON}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontSize: 13.5 }}>{r.title}</b>
                  <div className="sub">{r.description || t("tenantRequests.noDetailsProvided")}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                {r.urgency === "urgent" && <span className="badge pending">{t("requests.urgentBadge")}</span>}
                <span className={`badge ${r.status}`}>{t(STATUS_KEY[r.status] ?? r.status)}</span>
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "visits" && (
        <>
          {visits.length === 0 && <div className="empty-state">{t("tenantRequests.emptyNoVisits")}</div>}
          {visits.map((v) => {
            const dateLabel = new Date(v.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
            return (
              <div className="req-card visit" key={v.id}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div className="req-icon visit">{VISIT_ICON}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ fontSize: 13.5 }}>{v.propertyLabel}</b>
                    <div className="sub">{v.propertyLocality}</div>
                  </div>
                  <span className={`badge ${v.status === "confirmed" ? "paid" : v.status === "declined" || v.status === "cancelled" ? "pending" : "vacant"}`}>
                    {t(VISIT_STATUS_KEY[v.status] ?? v.status)}
                  </span>
                </div>
                <div className="sub" style={{ marginTop: 8 }}>
                  {t("tenantRequests.visitLine", { date: dateLabel, time: v.time })}
                </div>
                {v.note && <div className="sub" style={{ marginTop: 4 }}>&ldquo;{v.note}&rdquo;</div>}
                {v.status === "pending" && (
                  <button className="btn-outline btn-sm" style={{ marginTop: 10 }} onClick={() => cancelVisit(v.id)}>
                    {t("tenantRequests.cancelVisitButton")}
                  </button>
                )}
              </div>
            );
          })}
        </>
      )}

      {tab === "vacate" && (activeTenancy || vacateRequests.length > 0) && (
        <VacateNoticeSection tenancy={activeTenancy} vacateRequests={vacateRequests} onChanged={load} />
      )}
    </>
  );
}

function VacateNoticeSection({
  tenancy,
  vacateRequests,
  onChanged,
}: {
  // Null once the tenancy has ended (see TenantRequests's activeTenancy —
  // GET /api/tenancies/mine only returns active ones) — a tenant can no
  // longer give a NEW notice at that point, but their past vacate history
  // should stay visible, so this section still renders for them.
  tenancy: Tenancy | null;
  vacateRequests: VacateRequest[];
  onChanged: () => void;
}) {
  const { t } = useLanguage();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [noticeDate, setNoticeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const active = vacateRequests.find((v) => v.status === "pending") ?? null;
  const past = vacateRequests.filter((v) => v.status !== "pending");
  const noticeDays = tenancy?.noticePeriodDays ?? 0;

  function vacateDateFor(fromDate: string): string {
    const d = new Date(fromDate + "T00:00:00");
    d.setDate(d.getDate() + noticeDays);
    return d.toISOString().slice(0, 10);
  }

  async function submit() {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/tenancy/vacate-request", { noticeDate, note });
      setAdding(false);
      setNote("");
      toast(t("tenantRequests.vacateNoticeSentToast"));
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  async function cancelNotice(id: string) {
    await api.patch(`/api/vacate-requests/${id}`, { status: "cancelled" });
    toast(t("tenantRequests.vacateNoticeCancelledToast"));
    onChanged();
  }

  function whatsappHref(v: VacateRequest): string | null {
    const ownerPhone = tenancy?.property?.ownerPhone;
    if (!ownerPhone) return null;
    const msg = t("tenantRequests.vacateWhatsappMessage", {
      owner: tenancy?.property?.ownerName ?? "",
      property: tenancy?.property?.label ?? "",
      noticeDate: fmtDate(v.noticeDate),
      vacateDate: fmtDate(v.vacateDate),
    });
    return `https://wa.me/91${ownerPhone}?text=${encodeURIComponent(msg)}`;
  }

  return (
    <>
      <div className="section-title">
        {t("tenantRequests.vacateNoticeHeading")}
        {!active && tenancy && (
          <small onClick={() => setAdding((v) => !v)}>{adding ? t("tenantRequests.cancelLink") : t("tenantRequests.giveNoticeLink")}</small>
        )}
      </div>

      {active && (
        <div className="req-card vacate">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div className="req-icon vacate">{VACATE_ICON}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 13.5 }}>{t("tenantRequests.vacateOnLabel", { date: fmtDate(active.vacateDate) })}</b>
              <div className="sub">{t("tenantRequests.noticeGivenOnLabel", { date: fmtDate(active.noticeDate) })}</div>
            </div>
            <span className="badge pending">{t(VACATE_STATUS_KEY[active.status] ?? active.status)}</span>
          </div>
          {active.note && <div className="sub" style={{ marginTop: 8 }}>&ldquo;{active.note}&rdquo;</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            {whatsappHref(active) && (
              <a
                className="btn-solid btn-sm"
                style={{ flex: 1, textAlign: "center", textDecoration: "none" }}
                href={whatsappHref(active)!}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("tenantRequests.shareWhatsappBtn")}
              </a>
            )}
            <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => cancelNotice(active.id)}>
              {t("tenantRequests.cancelNoticeBtn")}
            </button>
          </div>
        </div>
      )}

      {!active && tenancy && adding && (
        <div className="card">
          {error && <div className="error-banner">{error}</div>}
          <div className="field">
            <label>{t("tenantRequests.noticeDateLabel")}</label>
            <div className="input-wrap"><input type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} /></div>
          </div>
          <div className="sub" style={{ marginBottom: 10 }}>
            {t("tenantRequests.vacateDatePreview", { date: fmtDate(vacateDateFor(noticeDate)), days: String(noticeDays) })}
          </div>
          <div className="field">
            <label>{t("tenantRequests.detailsOptional")}</label>
            <div className="input-wrap"><textarea value={note} onChange={(e) => setNote(e.target.value)} /></div>
          </div>
          <button className="btn-solid" disabled={busy} onClick={submit}>
            {busy ? t("tenantRequests.submittingBtn") : t("tenantRequests.submitVacateNoticeBtn")}
          </button>
        </div>
      )}

      {!active && !adding && past.length === 0 && (
        <div className="empty-state">{t("tenantRequests.emptyNoVacateNotices")}</div>
      )}

      {past.map((v) => (
        <div className="req-card vacate" key={v.id}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div className="req-icon vacate">{VACATE_ICON}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 13.5 }}>{t("tenantRequests.vacateOnLabel", { date: fmtDate(v.vacateDate) })}</b>
              <div className="sub">{t("tenantRequests.noticeGivenOnLabel", { date: fmtDate(v.noticeDate) })}</div>
            </div>
            <span className={`badge ${v.status === "acknowledged" ? "paid" : "pending"}`}>{t(VACATE_STATUS_KEY[v.status] ?? v.status)}</span>
          </div>
        </div>
      ))}
    </>
  );
}
